import React, { useEffect } from 'react';

import dayjs from 'dayjs';
import { findIndex, isEmpty as _isEmpty } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams, useLocation } from 'react-router-dom';

import AvatarTeacherIcon from '@/assets/icons/avatar-teacher.svg?react';
import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import UiStackTemplate from '@/components/layouts/UiStackTemplate';
import Jumbotron from '@/components/ui/box/Jumbotron';
import JumbotronDescription from '@/components/ui/box/JumbotronDescription';
import JumbotronPanel from '@/components/ui/box/JumbotronPanel';
import JumbotronTitle from '@/components/ui/box/JumbotronTitle';
import Paper from '@/components/ui/box/Paper';
import Filter from '@/components/ui/filter/Filter';
import PageTitle from '@/components/ui/headers/PageTitle';
import List from '@/components/ui/lists/List';
import ListItem from '@/components/ui/lists/ListItem';
import Pagination from '@/components/ui/lists/Pagination';
import { pageTitles } from '@/constants/pageTitles';
import useFetch from '@/hooks/common/useFetch';
import useCurrentUser from '@/hooks/store/useCurrentUser';
import { type Announcement } from '@/interfaces/announcement';
import { type ParentWithStudents } from '@/interfaces/parent';

import CSSModule from './AnnouncementsIndexPage.module.scss';

export type AnnouncementForIndex = Omit<
  Announcement,
  'announcement_files' | 'recipients'
>;

export interface AnnouncementsResult {
  announcements: AnnouncementForIndex[];
  pagination: {
    current_page: number;
    per_page: number;
    total_count: number;
  };
}

const AnnouncementsIndexPage: React.FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser] = useCurrentUser();
  const { organization_memberships: students } =
    currentUser as ParentWithStudents;

  const filters = [
    intl.formatMessage({
      id: 'announcements.announcementIndexPage.filter.all',
    }),
  ].concat(students.map(({ name }) => name));

  const page = searchParams.get('page') ?? 1;
  const studentId = searchParams.get('student_id') ?? '';

  const currentFilterIndex = studentId
    ? findIndex(students, (s) => s.id === studentId) + 1
    : 0;

  const {
    fetch: fetchAnnouncements,
    isFetching,
    status,
    data,
  } = useFetch<AnnouncementsResult>(
    'POST',
    '/api/school_communication/announcements',
  );

  // pagination
  const handleOnPageChanged = (page: number) => {
    setSearchParams({ page: page.toString(), student_id: studentId });
  };

  const handleFilterChange = ({ index }: { index: number }) => {
    const studentId = students[index - 1]?.['id'] ?? '';
    setSearchParams({ student_id: studentId });
  };

  useEffect(() => {
    void fetchAnnouncements({
      body: {
        page,
        organization_membership_ids: studentId ? [studentId] : null,
      },
    });
  }, [fetchAnnouncements, page, studentId]);

  const { announcements, pagination } = data || {
    announcements: [] as AnnouncementForIndex[],
  };

  const isLoading = !status || isFetching;
  const isError = !!(status && (status >= 500 || status === 404));
  const isRestricted = !!(status && status === 400);
  const isEmpty = _isEmpty(announcements);

  return (
    <SingleColumnTemplate>
      <Helmet>
        <title>{pageTitles.announcements}</title>
      </Helmet>
      <UiStackTemplate isLoading={isLoading} isError={isError} status={status}>
        <PageTitle
          title={intl.formatMessage({
            id: 'announcements.announcementIndexPage.title',
          })}
        />

        <div className={CSSModule.AnnouncementsIndexPage__Contents}>
          <Jumbotron>
            <div
              className={CSSModule.AnnouncementsIndexPage__Filter}
              aria-label="Filter announcements by student"
              data-testid="announcements_filter"
            >
              <Filter
                items={filters}
                onChange={handleFilterChange}
                initialIndex={currentFilterIndex}
              />
            </div>
            {isRestricted ? (
              <JumbotronPanel>
                <JumbotronTitle>お知らせ機能を利用できません</JumbotronTitle>
                <JumbotronDescription>
                  契約手続きが未完了もしくは団体会員コード未登録のため利用できません。学校にお問い合わせください。
                </JumbotronDescription>
              </JumbotronPanel>
            ) : isEmpty ? (
              <JumbotronPanel>
                <JumbotronTitle>
                  <FormattedMessage id="announcements.announcementIndexPage.empty.title" />
                </JumbotronTitle>
                <JumbotronDescription>
                  <FormattedMessage id="announcements.announcementIndexPage.empty.description" />
                </JumbotronDescription>
              </JumbotronPanel>
            ) : (
              <>
                <Paper>
                  <List>
                    {announcements.map(
                      ({ id, title, sent_ts, sender_user, read, important }) =>
                        id && ( // undefinedがわたっている可能性 https://studysapuri.sentry.io/issues/4956121444/?project=5430472
                          <ListItem
                            key={id}
                            icon={
                              sender_user?.profile_image_url ? (
                                <img src={sender_user.profile_image_url} />
                              ) : (
                                <AvatarTeacherIcon />
                              )
                            }
                            title={
                              important ? (
                                <>
                                  <span className={CSSModule.importantLabel}>
                                    【重要】
                                  </span>
                                  {title}
                                </>
                              ) : (
                                title
                              )
                            }
                            time={dayjs.unix(sent_ts).format(
                              intl.formatMessage({
                                id: 'announcements.announcementIndexPage.announcements.list_item.sent_at_format',
                              }),
                            )}
                            description={
                              sender_user?.display_name ??
                              intl.formatMessage({
                                id: 'announcements.announcementIndexPage.announcements.list_item.deleted_author_name',
                              })
                            }
                            url={`/announcements/${id}${location.search}`}
                            style={read ? 'read' : 'unread'}
                          />
                        ),
                    )}
                  </List>
                </Paper>
                {pagination && (
                  <div
                    className={CSSModule.AnnouncementsIndexPage__Pagination}
                    data-testid="announcements_pagination"
                  >
                    <Pagination
                      initialPage={pagination.current_page}
                      totalPage={Math.ceil(
                        pagination.total_count / pagination.per_page,
                      )}
                      onPageChanged={(page) => handleOnPageChanged(page)}
                    />
                  </div>
                )}
              </>
            )}
          </Jumbotron>
        </div>
      </UiStackTemplate>
    </SingleColumnTemplate>
  );
};

export default AnnouncementsIndexPage;
